import { useAcceptCgu } from "@/api/user";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg?react";
import CloseIcon from "@/assets/icons/close.svg?react";
import { blue } from "@/utils/Colors";
import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useNavigate } from "react-router-dom";
import Button from "../generic/Button";

import { cguFile, cguVersion } from "@/assets/cgu/CGU_2025-05-27";
import { useIntersection } from "@mantine/hooks";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

interface AcceptCGUModalProps {
  open: boolean;
  userId: string;
}

export default function AcceptCGUModal(props: Readonly<AcceptCGUModalProps>) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [numPages, setNumPages] = useState<number>(2);
  const [completeRead, setCompleteRead] = useState<boolean>(false);

  const acceptGcuMutation = useAcceptCgu({});

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setCompleteRead(false);
  }

  const lastPostRef = useRef<HTMLElement>(null);
  const { ref, entry } = useIntersection({
    root: lastPostRef.current,
    threshold: 1,
  });

  useEffect(() => {
    if (entry?.isIntersecting) {
      setCompleteRead(true);
    }
  }, [entry]);

  return (
    <Dialog
      open={props.open}
      onClose={() => navigate("/logout")}
      PaperProps={{
        component: "form",
        onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();

          await acceptGcuMutation.mutateAsync({
            cguVersion: cguVersion,
            userId: props.userId,
          });
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: blue[100] }}>
        <Typography variant="h6">{t("newCguToValidate")}</Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => navigate("/logout")}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          padding: 2,
          paddingBottom: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Document
                file={cguFile}
                onLoadSuccess={onDocumentLoadSuccess}
                options={options}
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      paddingY: 1,
                      ...(index !== numPages - 1 && {
                        borderBottom: "1px solid",
                        borderColor: blue[200],
                      }),
                    }}
                    key={`page_${index + 1}`}
                  >
                    <Page pageNumber={index + 1} />
                  </Box>
                ))}
              </Document>
            </Box>

            <Box ref={ref}></Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingBottom: 2 }}>
        <Button
          variant="primary"
          type="submit"
          endIcon={<ArrowRightIcon />}
          disabled={acceptGcuMutation.isPending || !completeRead}
        >
          {t("acceptGgu")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
